import { Injectable } from '@angular/core';

export type RouteGroup = 'home' | 'accounting' | 'settings' | 'account' | 'toolbox' | null;
export type AccountingGroup =
  | 'order-view'
  | 'orders'
  | 'payments'
  | 'repayments'
  | 'corrections'
  | 'bundles'
  | 'balance'
  | 'payouts'
  | 'returns'
  | 'account-balance'
  | 'gc-transactions'
  | 'reports'
  | 'accounting-entries'
  | null;

export type ToolboxGroup = 'balance' | 'account-balance' | 'reports' | null;

@Injectable({
  providedIn: 'root',
})
export class RouteMappingService {
  private readonly routeGroups = new Map<RouteGroup, string[]>([
    ['settings', ['integrations']],
    [
      'accounting',
      [
        'ordrer',
        'betalinger',
        'refunderinger',
        'tilbage-betalinger',
        'bank',
        'korrektioner',
        'bundles',
        'fulfillments',
        'returns',
        'transaktioner',
        'accounting-entries',
      ],
    ],
    ['toolbox', ['rapporter', 'saldobalance', 'balances']],
    ['account', ['konto']],
    ['home', ['']],
  ]);

  private readonly accountingGroups = new Map<AccountingGroup, string[]>([
    ['order-view', ['/ordrer/']],
    ['orders', ['/ordrer', '/refunderinger', '/fulfillments', '/returns']],
    ['payments', ['/betalinger/salgskanal', '/betalinger/gateway', '/betalinger/gavekort']],
    ['repayments', ['/tilbage-betalinger/salgskanal', '/tilbage-betalinger/gateway', '/tilbage-betalinger/gavekort']],
    ['corrections', ['/korrektioner']],
    ['bundles', ['/bundles']],
    ['payouts', ['/bank']],
    ['gc-transactions', ['/transaktioner']],
    ['accounting-entries', ['/accounting-entries']],
  ]);

  private readonly toolboxGroups = new Map<ToolboxGroup, string[]>([
    ['balance', ['/balances/orders-postings']],
    ['account-balance', ['/saldobalance']],
    ['reports', ['/rapporter']],
  ]);

  public readonly orderTabGroupItems = [
    { label: 'Ordrer', route: '/ordrer' },
    { label: 'Refunderinger', route: '/refunderinger' },
    { label: 'Fulfillments', route: '/fulfillments' },
    { label: 'Returns', route: '/returns' },
  ];

  public readonly paymentsTabGroupItems = [
    { label: 'Betalingsgateway', route: '/betalinger/gateway' },
    { label: 'Salgskanal', route: '/betalinger/salgskanal' },
    { label: 'Gavekort', route: '/betalinger/gavekort' },
  ];

  public readonly repaymentsTabGroupItems = [
    { label: 'Betalingsgateway', route: '/tilbage-betalinger/gateway' },
    { label: 'Salgskanal', route: '/tilbage-betalinger/salgskanal' },
    { label: 'Gavekort', route: '/tilbage-betalinger/gavekort' },
  ];

  public readonly balanceTabGroupItems = [{ label: 'Posteringsbalance', route: '/balances/orders-postings' }];

  public readonly tabGroupItemsMap = {
    orders: this.orderTabGroupItems,
    payments: this.paymentsTabGroupItems,
    repayments: this.repaymentsTabGroupItems,
    balance: this.balanceTabGroupItems,
  };

  getRouteGroup(url: string): RouteGroup {
    const cleanUrl = this.removeQueryParameters(url);

    if (cleanUrl === '/') return 'home';

    for (const [group, paths] of this.routeGroups.entries()) {
      for (const path of paths) {
        if (cleanUrl === `/${path}` || cleanUrl.includes(`/${path}/`)) {
          return group;
        }
      }
    }

    return null;
  }

  getAccountingGroup(url: string): AccountingGroup {
    const cleanUrl = this.removeQueryParameters(url);

    return (
      Array.from(this.accountingGroups.entries()).find(([_, paths]) =>
        paths.some((path) => cleanUrl.startsWith(path)),
      )?.[0] || null
    );
  }

  getToolboxGroup(url: string): ToolboxGroup {
    const cleanUrl = this.removeQueryParameters(url);

    return (
      Array.from(this.toolboxGroups.entries()).find(([_, paths]) =>
        paths.some((path) => cleanUrl.startsWith(path)),
      )?.[0] || null
    );
  }

  getTabGroupItems(accountingGroup: AccountingGroup): any[] {
    return this.tabGroupItemsMap[accountingGroup as keyof typeof this.tabGroupItemsMap] || [];
  }

  removeQueryParameters(url: string): string {
    const [baseUrl, hash] = url.split('#');
    const cleanUrl = baseUrl.split('?')[0];

    return hash ? `${cleanUrl}#${hash}` : cleanUrl;
  }
}
